import type { ReactNode } from 'react'
import { useAuthStore } from '@/store/auth'
import { useAppStore } from '@/store/app'
import { useSelfStatus } from '@/store/presence'
import { avatarUrl, userBannerUrl } from '@/lib/avatar'
import { useProfileBanner } from '@/lib/profileBanner'
import { cx } from '@/lib/cx'
import { Avatar } from './Avatar'
import { StatusIcon } from './StatusIcon'
import { StyledName } from './StyledName'
import styles from './SelfCard.module.css'

interface Props {
  /** Текст в пузыре: свой статус или подсказка, если его нет. */
  bubble: string
  /** Пузырь с подсказкой, а не со статусом: курсив и приглушённый цвет. */
  placeholder: boolean
  /** Пузырь-кнопка (меню статуса открывает по нему окно); без него — просто картинка. */
  onBubble?: (() => void) | undefined
  /** Что стоит справа от @ника: значки в превью окна статуса. */
  extra?: ReactNode | undefined
  /** Длинный статус — в две строки (превью окна), а не многоточием в одну (меню). */
  wrap?: boolean
  className?: string | undefined
}

/**
 * Своя карточка, как в Discord: баннер, аватар с рамкой и статусом, пузырь своего статуса, имя
 * и @ник. Одна и та же — сверху меню статуса и превью в окне «Свой статус». Цвет кольца вокруг
 * аватара — фон того, где карточка лежит (--self-card-bg).
 */
export function SelfCard({ bubble, placeholder, onBubble, extra, wrap = false, className }: Props) {
  const user = useAuthStore((s) => s.user)
  const online = useAppStore((s) => s.gateway) === 'ready'
  const status = useSelfStatus()
  const name = user?.global_name ?? user?.username ?? '…'
  const banner = user ? userBannerUrl(user.id, user.banner) : undefined
  const avatar = user ? avatarUrl(user.id, user.avatar) : undefined
  const bannerStyle = useProfileBanner(banner, avatar)
  const bubbleClass = cx(styles.bubble, placeholder && styles.bubbleEmpty)
  const text = <span className={styles.bubbleText}>{bubble}</span>

  return (
    <div className={cx(styles.card, className)} data-wrap={wrap ? '' : undefined}>
      <div className={styles.banner} style={bannerStyle} />
      <div className={styles.head}>
        <span className={styles.avatar}>
          <Avatar initial={name.slice(0, 1).toUpperCase()} color="#5865F2" size={64} className={styles.avatarImage} src={avatar} decoration={user?.avatar_decoration_data} />
          <span className={styles.status}><StatusIcon status={online ? status : 'offline'} size={14} /></span>
        </span>
        {onBubble ? (
          <button
            type="button"
            role="menuitem"
            className={bubbleClass}
            data-tooltip={placeholder ? undefined : bubble}
            aria-label={placeholder ? 'Свой статус' : `Свой статус: ${bubble}`}
            onClick={onBubble}
          >
            {text}
          </button>
        ) : (
          <div className={bubbleClass} aria-hidden="true">{text}</div>
        )}
      </div>
      <div className={styles.names}>
        <StyledName name={name} style={user?.display_name_styles} className={styles.name} />
        <span className={styles.usernameRow}>
          <span className={styles.username}>@{user?.username}</span>
          {extra}
        </span>
      </div>
    </div>
  )
}
